import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const CategoryModal = ({ isOpen, onClose, onSubmit, initialData, title, categoryType }) => {
  const [name, setName] = useState('');
  const [tileLengthMm, setTileLengthMm] = useState('');
  const [tileWidthMm, setTileWidthMm] = useState('');
  const [piecesPerCarton, setPiecesPerCarton] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialData) {
      setName(initialData.name || initialData.dimension || '');
      // For sizes, load tile dimension data if available
      if (categoryType === 'size') {
        setTileLengthMm(initialData.tile_length_mm || '');
        setTileWidthMm(initialData.tile_width_mm || '');
        setPiecesPerCarton(initialData.default_pieces_per_carton || '');
      }
    } else {
      setName('');
      setTileLengthMm('');
      setTileWidthMm('');
      setPiecesPerCarton('');
    }
    setError('');
  }, [initialData, isOpen, categoryType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    // Build submission data based on category type
    let submitData = { name: name.trim() };

    if (categoryType === 'size') {
      // Add tile dimension fields for size category
      if (tileLengthMm) submitData.tile_length_mm = parseInt(tileLengthMm);
      if (tileWidthMm) submitData.tile_width_mm = parseInt(tileWidthMm);
      if (piecesPerCarton) submitData.default_pieces_per_carton = parseInt(piecesPerCarton);
    }

    setLoading(true);
    try {
      await onSubmit(submitData);
      setName('');
      setTileLengthMm('');
      setTileWidthMm('');
      setPiecesPerCarton('');
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-primary-black">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Name/Dimension Field */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {categoryType === 'size' ? 'Dimension (e.g., 40x40)' : 'Name'} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={categoryType === 'size' ? '40x40' : 'Enter name...'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
              disabled={loading}
            />
          </div>

          {/* Tile Dimension Fields - Only for Size Category */}
          {categoryType === 'size' && (
            <>
              <div className="mb-4 p-4 bg-secondary-offWhite rounded-lg">
                <h3 className="text-sm font-semibold text-primary-slate mb-3">
                  Tile Details for Calculator
                  <span className="text-xs font-normal text-gray-500 ml-2">(Optional)</span>
                </h3>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Tile Length (mm)
                    </label>
                    <input
                      type="number"
                      value={tileLengthMm}
                      onChange={(e) => setTileLengthMm(e.target.value)}
                      placeholder="e.g., 400"
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent text-sm"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Tile Width (mm)
                    </label>
                    <input
                      type="number"
                      value={tileWidthMm}
                      onChange={(e) => setTileWidthMm(e.target.value)}
                      placeholder="e.g., 400"
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent text-sm"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Pieces per Carton
                  </label>
                  <input
                    type="number"
                    value={piecesPerCarton}
                    onChange={(e) => setPiecesPerCarton(e.target.value)}
                    placeholder="e.g., 4"
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent text-sm"
                    disabled={loading}
                  />
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  These values will auto-fill when this size is selected in the calculator.
                </p>
              </div>
            </>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : initialData ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryModal;
